import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Loader2, Eye, EyeOff, Check, ArrowLeft, ShieldCheck } from 'lucide-react';

/**
 * Public password-reset landing page.
 *
 * Flow:
 *  1. User clicks the email link from Supabase, which appends a recovery token
 *     to the URL hash (e.g. #access_token=...&type=recovery).
 *  2. Supabase's auth client automatically parses the hash and fires a
 *     `PASSWORD_RECOVERY` event — at that point the user has a temporary
 *     authenticated session that may ONLY be used to call updateUser({ password }).
 *  3. We capture that event, show the new-password form, and on submit call
 *     supabase.auth.updateUser to set the new password.
 *  4. After success we sign the temporary recovery session out and send the
 *     user to the home page so they can sign in cleanly with the new password.
 */
const ResetPassword: React.FC = () => {
  const navigate = useNavigate();

  const [ready, setReady] = useState(false);
  const [recoveryDetected, setRecoveryDetected] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Listen for the recovery event Supabase fires after parsing the URL hash.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryDetected(true);
        setReady(true);
      }
    });

    // Also check whether a session already exists (covers the case where the
    // event has already fired before this component mounted).
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setRecoveryDetected(true);
      }
      setReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (err) {
      setError(err.message);
      return;
    }

    setSuccess(true);
    // Sign the temporary recovery session out so the user must sign in fresh.
    await supabase.auth.signOut();
    setTimeout(() => navigate('/'), 2500);
  };

  const inputClass =
    'w-full bg-white border border-[#E8DFD0] px-4 py-3.5 font-sans text-sm text-[#2C2C2C] placeholder:text-[#bbb] focus:outline-none focus:border-[#C9A961] transition-colors';

  return (
    <div className="min-h-screen bg-[#FAF8F3] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md shadow-xl border border-[#E8DFD0] overflow-hidden">
        {/* Header */}
        <div className="text-center pt-10 pb-6 px-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-8 h-[1px] bg-[#C9A961]" />
            <span className="font-serif text-xl tracking-[0.3em] font-light text-[#1A3B5C]">ZERA</span>
            <div className="w-8 h-[1px] bg-[#C9A961]" />
          </div>
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#1A3B5C]/5 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-[#1A3B5C]" />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl text-[#1A3B5C] font-light mb-2">
            Choose a New Password
          </h1>
          <p className="font-sans text-xs text-[#999]">
            Pick a strong password you haven't used before.
          </p>
        </div>

        {/* States */}
        {!ready && (
          <div className="px-8 pb-10 flex items-center justify-center gap-2 text-[#999] font-sans text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Verifying reset link...
          </div>
        )}

        {ready && !recoveryDetected && !success && (
          <div className="px-8 pb-10 space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 text-red-600 font-sans text-xs leading-relaxed">
              This password reset link is invalid or has expired. Please request a new one from the sign-in page.
            </div>
            <button
              onClick={() => navigate('/')}
              className="w-full flex items-center justify-center gap-2 font-sans text-xs text-[#999] hover:text-[#1A3B5C] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Home
            </button>
          </div>
        )}

        {ready && recoveryDetected && !success && (
          <form onSubmit={handleSubmit} className="px-8 pb-10 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-600 font-sans text-xs">
                {error}
              </div>
            )}

            <div>
              <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-12`}
                  placeholder="Min. 6 characters"
                  autoComplete="new-password"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#bbb] hover:text-[#666] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                Confirm New Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={inputClass}
                placeholder="Re-enter your new password"
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-[#C9A961] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-[#b8944d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-4"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Updating...
                </>
              ) : (
                'Update Password'
              )}
            </button>
          </form>
        )}

        {success && (
          <div className="px-8 pb-10 space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 text-green-700 font-sans text-xs flex items-start gap-2">
              <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Password updated successfully. Redirecting you to the home page so you can sign in...</span>
            </div>
          </div>
        )}

        {/* Decorative bottom */}
        <div className="h-1 bg-gradient-to-r from-[#1A3B5C] via-[#C9A961] to-[#1A3B5C]" />
      </div>
    </div>
  );
};

export default ResetPassword;
